import { useContext, useEffect, useRef, useState } from 'react';
import { VaultNewRecordsContext } from './context';

const NO_IDS: ReadonlySet<number> = new Set();

/**
 * [MCV-1812] The ids to show a "New" pill on, for one vault table.
 *
 * `rows` is the data the grid is rendering. A record counts as seen only once its row is in it, so
 * a request that failed or came back empty (no rows) never clears a badge for a row the provider
 * was not shown. Only the ids that were rendered are marked seen.
 *
 * The pills stay for the whole visit: the ids shown are kept here, so they are gone only the next
 * time the section mounts. A parse that finishes while the section is open adds its records on top
 * (and marks those seen once). Pass `undefined` for a grid that has no table (no pills, nothing
 * marked).
 */
export const useNewRecordPills = (
  tableName: string | undefined,
  rows: ReadonlyArray<{ id?: unknown }> | null | undefined
): ReadonlySet<number> => {
  const { byTable, markSeen } = useContext(VaultNewRecordsContext);
  const unseen = tableName ? byTable[tableName] : undefined;
  const [shown, setShown] = useState<ReadonlySet<number>>(NO_IDS);
  const shownRef = useRef<ReadonlySet<number>>(NO_IDS);

  useEffect(() => {
    if (!tableName || !unseen?.length || !rows?.length) return;
    const rendered = new Set<number>();
    for (const row of rows) if (row.id != null) rendered.add(Number(row.id));
    const fresh = unseen.filter((id) => rendered.has(id) && !shownRef.current.has(id));
    if (fresh.length === 0) return;
    shownRef.current = new Set([...shownRef.current, ...fresh]);
    setShown(shownRef.current);
    markSeen({ [tableName]: fresh });
  }, [tableName, rows, unseen, markSeen]);

  return tableName ? shown : NO_IDS;
};
